import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { readerApi, familyApi, reportApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ReaderSelector from '../components/reporting/ReaderSelector';
import ProgressContextRow from '../components/progress/ProgressContextRow';
import YearScopeToggle from '../components/progress/YearScopeToggle';
import ReportsToolbar from '../components/reports/ReportsToolbar';
import ReportList from '../components/reports/ReportList';
import BulkActionBar from '../components/reports/BulkActionBar';
import { hasSimplifiedSetup } from '../utils/userSetup';
import { buildCopyRequests } from '../utils/bulkReports';
import { groupReportsByDay } from '../utils/reportGroups';
import '../components/reports/reports.css';
import './ManageReportsPage.css';

const REPORTS_PER_PAGE = 100;
const NO_FILTERS = { startDate: '', endDate: '', testament: '', book: '' };

const plural = (count) => `${count} report${count === 1 ? '' : 's'}`;

const ManageReportsPage = () => {
  const [readers, setReaders] = useState([]);
  const [families, setFamilies] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingReports, setLoadingReports] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedReaders, setSelectedReaders] = useState([]);
  const [readerMode, setReaderMode] = useState('all'); // 'all', 'primary', 'family', 'specific'
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // 'season' shows this year's reading only; 'all' shows every year.
  const [yearScope, setYearScope] = useState('season');

  // Reports are loaded a page at a time and appended; this is how many are in.
  const [loadedPages, setLoadedPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const requestRef = useRef(0);

  const [editingReport, setEditingReport] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [bulkTargetReaderId, setBulkTargetReaderId] = useState('');
  const [bulkWorking, setBulkWorking] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Any change to who, when or what starts the list over.
  useEffect(() => {
    setSelectedReports([]);
    setEditingReport(null);
    loadFresh();
  }, [selectedReaders, filters, yearScope]);

  const isSimplifiedSetup = hasSimplifiedSetup(readers, families);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [readersRes, familiesRes] = await Promise.all([
        readerApi.getReaders(),
        familyApi.getFamilies()
      ]);

      const readersData = readersRes.data.readers || [];
      const familiesData = familiesRes.data.families || [];

      setReaders(readersData);
      setFamilies(familiesData);

      // For users with simplified setup, default to primary reader mode
      if (hasSimplifiedSetup(readersData, familiesData)) {
        const primaryReader = readersData.find(r => r.isPrimary);
        if (primaryReader) {
          setReaderMode('primary');
          setSelectedReaders([primaryReader]);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Failed to load initial data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const buildParams = (page) => {
    const params = { page, limit: REPORTS_PER_PAGE };
    if (selectedReaders.length === 1) {
      params.readerId = selectedReaders[0].id;
    } else if (selectedReaders.length > 1) {
      params.readerIds = selectedReaders.map(reader => reader.id).join(',');
    }
    if (yearScope === 'season') params.season = 'current';
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.book) params.book = filters.book;
    if (filters.testament) params.testament = filters.testament;
    return params;
  };

  // Loads pages 1..count (or just page `count` when appending). Only the
  // newest request may write to the list, so a slow response for an old
  // filter can't overwrite a newer one.
  const loadPages = async (count, { append = false } = {}) => {
    const requestId = ++requestRef.current;
    try {
      setLoadingReports(true);
      const first = append ? count : 1;
      const responses = await Promise.all(
        Array.from({ length: count - first + 1 }, (_, i) => reportApi.getReports(buildParams(first + i)))
      );
      if (requestId !== requestRef.current) return;

      const incoming = responses.flatMap(response => response.data.reports || []);
      setReports(prev => {
        if (!append) return incoming;
        const have = new Set(prev.map(report => report.id));
        return [...prev, ...incoming.filter(report => !have.has(report.id))];
      });
      setLoadedPages(count);
      setTotalReports(responses[responses.length - 1].data.pagination?.totalReports || 0);
      setError(null);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error('Failed to load reports:', err);
      setError('Failed to load reports. Please try again.');
    } finally {
      if (requestId === requestRef.current) setLoadingReports(false);
    }
  };

  const loadFresh = () => loadPages(1);
  const loadMore = () => loadPages(loadedPages + 1, { append: true });
  // After an edit, delete or bulk action: re-fetch everything already loaded
  // so the list doesn't jump back to the first page.
  const refreshLoaded = () => loadPages(loadedPages);

  const handleReaderModeChange = (mode) => {
    setReaderMode(mode);
    setSelectedReaders([]);
    setSelectedFamily(null);

    if (mode === 'primary') {
      const primaryReader = readers.find(r => r.isPrimary);
      if (primaryReader) {
        setSelectedReaders([primaryReader]);
      }
    }
  };

  const handleFamilySelect = (family) => {
    setSelectedFamily(family);
    setSelectedReaders(family?.readers || []);
  };

  const handleReaderToggle = (reader) => {
    setSelectedReaders(prev => (
      prev.some(r => r.id === reader.id)
        ? prev.filter(r => r.id !== reader.id)
        : [...prev, reader]
    ));
  };

  const clearFilters = () => setFilters(NO_FILTERS);

  const showSuccess = (message, ms = 3000) => {
    setSuccessMessage(message);
    setError(null);
    setTimeout(() => setSuccessMessage(''), ms);
  };

  const handleEditReport = (report) => {
    setEditingReport({
      id: report.id,
      notes: report.notes || '',
      readingDetails: {
        translation: report.translation || '',
        readingMethod: report.readingMethod || '',
        audioSpeed: report.audioSpeed || '',
        readingDuration: report.readingDuration || '',
        comprehensionLevel: report.comprehensionLevel || '',
        emotionalImpact: report.emotionalImpact || '',
        studyAids: report.studyAids || [],
        location: report.location || '',
        timeOfDay: report.timeOfDay || '',
        distractions: report.distractions || '',
        prayerTime: report.prayerTime || false,
        journaling: report.journaling || false
      }
    });
  };

  const handleSaveReport = async () => {
    if (!editingReport) return;

    try {
      await reportApi.updateReport(editingReport.id, {
        notes: editingReport.notes,
        ...editingReport.readingDetails
      });
      showSuccess('Report updated');
      setEditingReport(null);
      refreshLoaded();
    } catch (err) {
      console.error('Failed to update report:', err);
      setError(err.response?.data?.error || 'Failed to update report. Please try again.');
    }
  };

  const handleCancelEdit = () => setEditingReport(null);

  const handleDeleteReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this reading report?')) {
      return;
    }

    try {
      await reportApi.deleteReport(reportId);
      showSuccess('Report deleted');
      setEditingReport(null);
      refreshLoaded();
    } catch (err) {
      console.error('Failed to delete report:', err);
      setError(err.response?.data?.error || 'Failed to delete report. Please try again.');
    }
  };

  const editActions = {
    start: handleEditReport,
    cancel: handleCancelEdit,
    change: setEditingReport,
    save: handleSaveReport,
    remove: handleDeleteReport
  };

  const toggleSelectMode = () => {
    setEditingReport(null);
    setSelectedReports([]);
    setSelectMode(on => !on);
  };

  const handleToggleSelect = (reportId) => {
    setSelectedReports(prev => (
      prev.includes(reportId) ? prev.filter(id => id !== reportId) : [...prev, reportId]
    ));
  };

  const handleToggleMany = (ids, checked) => {
    setSelectedReports(prev => {
      const rest = prev.filter(id => !ids.includes(id));
      return checked ? [...rest, ...ids] : rest;
    });
  };

  const allSelected = reports.length > 0 && selectedReports.length === reports.length;
  const handleSelectAll = () => {
    setSelectedReports(allSelected ? [] : reports.map(report => report.id));
  };

  const finishBulk = (message) => {
    showSuccess(message, 5000);
    setSelectedReports([]);
    refreshLoaded();
  };

  const handleBulkDelete = async () => {
    if (selectedReports.length === 0) return;
    if (!confirm(`Delete ${plural(selectedReports.length)}? This can't be undone.`)) {
      return;
    }

    try {
      setBulkWorking(true);
      const response = await reportApi.bulkDeleteReports(selectedReports);
      finishBulk(`Deleted ${plural(response.data.deleted ?? selectedReports.length)}.`);
    } catch (err) {
      console.error('Failed to delete reports:', err);
      setError(err.response?.data?.error || 'Failed to delete reports. Nothing was deleted.');
    } finally {
      setBulkWorking(false);
    }
  };

  // Log the selected chapters for another reader, on the same days. With
  // move, the originals are deleted once every copy has been saved.
  const handleBulkCopy = async ({ move }) => {
    const target = readers.find(reader => reader.id === bulkTargetReaderId);
    if (!target || selectedReports.length === 0) return;

    const chosen = reports.filter(report => selectedReports.includes(report.id));
    const { requests, skipped } = buildCopyRequests(chosen, target.id);
    const copies = chosen.length - skipped;
    if (copies === 0) {
      setError(`Those reports already belong to ${target.name}.`);
      return;
    }

    const verb = move ? 'Move' : 'Also log';
    if (!confirm(`${verb} ${plural(copies)} for ${target.name}?`)) {
      return;
    }

    try {
      setBulkWorking(true);
      for (const request of requests) {
        await reportApi.createReport(request);
      }
      if (move) {
        const originals = chosen
          .filter(report => (report.readerId ?? report.reader?.id) !== target.id)
          .map(report => report.id);
        try {
          await reportApi.bulkDeleteReports(originals);
        } catch (deleteErr) {
          console.error('Copied but failed to remove originals:', deleteErr);
          setError(`Logged ${plural(copies)} for ${target.name}, but the originals could not be removed. Delete them to finish the move.`);
          refreshLoaded();
          return;
        }
      }
      finishBulk(`${move ? 'Moved' : 'Logged'} ${plural(copies)} ${move ? 'to' : 'for'} ${target.name}.`);
    } catch (err) {
      console.error('Failed to copy reports:', err);
      setError(err.response?.data?.error || `Failed to log reports for ${target.name}. Some may have been saved; check their reports before retrying.`);
      refreshLoaded();
    } finally {
      setBulkWorking(false);
    }
  };

  const groups = useMemo(() => groupReportsByDay(reports), [reports]);
  const selectedIds = useMemo(() => new Set(selectedReports), [selectedReports]);
  const hasFilters = Object.values(filters).some(Boolean);
  const showReader = readers.length > 1 && selectedReaders.length !== 1;

  const readerLabel = readerMode === 'all' || selectedReaders.length === 0
    ? 'All readers'
    : selectedReaders.length <= 3
      ? selectedReaders.map(reader => reader.name).join(', ')
      : `${selectedReaders.length} readers`;

  if (loading) {
    return (
      <div className="manage-reports-page loading">
        <LoadingSpinner size="large" message="Loading reports..." />
      </div>
    );
  }

  return (
    <div className="manage-reports-page">
      <ProgressContextRow
        label={isSimplifiedSetup ? 'Your reports' : readerLabel}
        picker={isSimplifiedSetup ? null : (
          <ReaderSelector
            readers={readers}
            families={families}
            readerMode={readerMode}
            selectedReaders={selectedReaders}
            selectedFamily={selectedFamily}
            onReaderModeChange={handleReaderModeChange}
            onFamilySelect={handleFamilySelect}
            onReaderToggle={handleReaderToggle}
            showAllOption={true}
          />
        )}
      >
        <YearScopeToggle value={yearScope} onChange={setYearScope} />
      </ProgressContextRow>

      <ReportsToolbar
        total={totalReports}
        filters={filters}
        onFiltersChange={(patch) => setFilters(prev => ({ ...prev, ...patch }))}
        onClearFilters={clearFilters}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen(open => !open)}
        selectMode={selectMode}
        onToggleSelect={toggleSelectMode}
        canSelect={reports.length > 0}
      />

      {(error || successMessage) && (
        <p
          className={`report-status ${error ? 'report-status--error' : 'report-status--success'}`}
          role={error ? 'alert' : 'status'}
        >
          {error || successMessage}
        </p>
      )}

      {reports.length === 0 && !loadingReports ? (
        <div className="report-empty">
          {hasFilters ? (
            <>
              <p>No reports match these filters.</p>
              <button type="button" className="report-btn" onClick={clearFilters}>Clear filters</button>
            </>
          ) : (
            <>
              <p>Nothing logged yet.</p>
              <Link to="/report" className="report-btn report-btn--primary">Log a reading</Link>
            </>
          )}
        </div>
      ) : (
        <>
          <ReportList
            groups={groups}
            showReader={showReader}
            selectMode={selectMode}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleMany={handleToggleMany}
            editing={editingReport}
            editActions={editActions}
          />

          <div className="report-more">
            <span>Showing {reports.length.toLocaleString()} of {totalReports.toLocaleString()}</span>
            {reports.length < totalReports && (
              <button
                type="button"
                className="report-btn"
                onClick={loadMore}
                disabled={loadingReports}
              >
                {loadingReports ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        </>
      )}

      {selectMode && (
        <BulkActionBar
          count={selectedReports.length}
          readers={readers}
          targetReaderId={bulkTargetReaderId}
          onTargetChange={setBulkTargetReaderId}
          onCopy={() => handleBulkCopy({ move: false })}
          onMove={() => handleBulkCopy({ move: true })}
          onDelete={handleBulkDelete}
          onSelectAll={handleSelectAll}
          allSelected={allSelected}
          working={bulkWorking}
        />
      )}
    </div>
  );
};

export default ManageReportsPage;
